/**
 * Streamlain: Bulk Upload Button for Database Tables
 * E018-S017: Allows multi-file upload with automatic row creation
 */

import { unsafeCSSVarV2 } from '@blocksuite/affine-shared/theme';
import { UploadIcon } from '@blocksuite/icons/lit';
import { css, html } from 'lit';

import { dataViewI18nT } from '../../../../core/i18n.js';
import { WidgetBase } from '../../../../core/widget/widget-base.js';

const styles = css`
  .bulk-upload {
    font-weight: 500;
  }

  .bulk-upload svg {
    font-size: 20px;
    color: ${unsafeCSSVarV2('icon/primary')};
  }
`;

/**
 * Custom event dispatched when files are selected for bulk upload.
 * Handled by React layer which has access to BlobEngine and DataSource.
 */
export interface BulkUploadEvent {
  files: File[];
  databaseId: string;
}

export function openDatabaseBulkUploadPicker(databaseId: string): void {
  const input = document.createElement('input');
  input.type = 'file';
  input.multiple = true;
  input.accept = '*/*';
  input.style.display = 'none';

  const cleanup = () => {
    input.removeEventListener('change', onFilesSelected);
    window.removeEventListener('focus', onWindowFocus);
    input.remove();
  };
  const onFilesSelected = () => {
    const files = input.files;
    if (files?.length) {
      window.dispatchEvent(
        new CustomEvent<BulkUploadEvent>('streamlain:database-bulk-upload', {
          detail: {
            files: Array.from(files),
            databaseId,
          },
        })
      );
    }
    cleanup();
  };
  const onWindowFocus = () => {
    window.setTimeout(() => {
      if (!input.files?.length) cleanup();
    }, 500);
  };

  input.addEventListener('change', onFilesSelected);
  window.addEventListener('focus', onWindowFocus, { once: true });
  document.body.appendChild(input);
  input.click();
}

declare global {
  interface WindowEventMap {
    'streamlain:database-bulk-upload': CustomEvent<BulkUploadEvent>;
  }
}

export class DataViewHeaderToolsBulkUpload extends WidgetBase {
  static override styles = styles;

  private get readonly$() {
    return this.view.readonly$;
  }

  private readonly onBulkUpload = () => {
    if (this.readonly$.value) return;
    const dataSource = this.view.manager.dataSource as { blockId?: string };
    openDatabaseBulkUploadPicker(dataSource.blockId ?? 'unknown');
  };

  override render() {
    if (this.readonly$.value) {
      return;
    }
    return html` <data-view-component-button
      class="affine-database-toolbar-item bulk-upload"
      .onClick="${this.onBulkUpload}"
      .icon="${UploadIcon()}"
      .text="${html`<span style="font-weight: 500"
        >${dataViewI18nT('toolbar.importFiles')}</span
      >`}"
    >
    </data-view-component-button>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'data-view-header-tools-bulk-upload': DataViewHeaderToolsBulkUpload;
  }
}
